import { useState, useEffect } from "react";
import { User, MapPin, Briefcase, Calendar, Camera, Edit, Save, GraduationCap, Languages, BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { refreshUserProfile } from "@/utils/supabase";
import { formatBioText } from "@/utils/textFormatting";
import { supabase } from "@/integrations/supabase/client";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ProfilePictureUploader from "@/components/ProfilePictureUploader";
import type { Course } from "@/types/course";
import { Link, useParams, useNavigate } from "react-router-dom";
import ReviewsSection from "@/components/ReviewsSection";
import CourseDialog from "@/components/CourseDialog";

const MentorProfile = () => {
  const { user, loading: authLoading, refreshUser } = useAuth();
  const { toast } = useToast();
  const { id } = useParams<{ id: string }>();
  const [isViewingOtherProfile, setIsViewingOtherProfile] = useState(false);
  const navigate = useNavigate();

  const [userData, setUserData] = useState({
    firstName: "",
    lastName: "",
    location: "",
    occupation: "",
    bio: "",
    shortSummary: "",
    joinDate: new Date().toLocaleDateString(),
    education: "",
    language: "",
    skills: [] as string[],
    profilePicture: "",
    coverPhoto: "/lovable-uploads/63560c3c-d7c3-4d91-b852-d2cd79229bf8.png",
  });

  const [isEditing, setIsEditing] = useState(false);
  const [newSkill, setNewSkill] = useState("");
  const [approvedCourses, setApprovedCourses] = useState<Course[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const loadApprovedCourses = async (profileId: string) => {
    try {
      console.log("Loading courses for profile ID:", profileId);
      
      const { data, error } = await supabase
        .from('courses')
        .select('*')
        .eq('mentor_id', profileId)
        .eq('status', 'approved')
        .eq('is_active', true);
      
      if (error) {
        console.error("Error loading approved courses:", error);
        return;
      }
      
      console.log("Fetched courses:", data);
      
      if (data && data.length > 0) {
        const mappedCourses: Course[] = data.map(course => ({
          id: course.id,
          title: course.title,
          description: course.description || '',
          courseType: course.course_type as 'subscription' | 'call',
          price: course.price,
          duration: course.duration,
          mentorId: course.mentor_id,
          status: course.status as 'pending' | 'approved' | 'rejected',
          createdAt: course.created_at,
          updatedAt: course.updated_at,
          category: course.course_type === 'subscription' ? 'Subscription' : 'Voice/Video Call',
          weeklyCallAllowance: course.weekly_call_allowance,
          subscriptionType: course.subscription_type as 'weekly' | 'monthly',
          isActive: course.is_active !== false
        }));
        setApprovedCourses(mappedCourses);
      } else {
        setApprovedCourses([]);
      }
    } catch (error) {
      console.error("Error loading approved courses:", error);
    }
  };

  useEffect(() => {
    // Only wait for auth loading if we're potentially going to edit the profile
    if (!id && authLoading) {
      return;
    }

    const loadUserData = async () => {
      setIsLoading(true);
      
      // Get the profile ID to view - either from URL param or current user
      const profileId = id || user?.id;
      
      // Set viewing state based on whether this is the current user's profile
      if ((id && (!user || id !== user.id)) || !user) {
        setIsViewingOtherProfile(true);
      } else {
        setIsViewingOtherProfile(false);
      }
      
      if (profileId) {
        try {
          const { data, error } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', profileId)
            .single();
          
          if (error) {
            console.error("Error fetching profile:", error);
            toast({
              title: "Error",
              description: "Failed to load mentor profile",
              variant: "destructive",
            });
            setIsLoading(false);
            return;
          }
          
          if (data) {
            let skills: string[] = [];
            if (data.specialties) {
              try {
                const parsed = JSON.parse(data.specialties);
                skills = Array.isArray(parsed) ? parsed : [parsed];
              } catch {
                if (typeof data.specialties === "string") {
                  skills = [data.specialties];
                }
              }
            }
            
            setUserData({
              firstName: data.first_name || "",
              lastName: data.last_name || "",
              location: data.location || "",
              occupation: data.profession || "",
              bio: data.bio || "",
              shortSummary: data.short_summary || "",
              joinDate: new Date(data.created_at).toLocaleDateString() || new Date().toLocaleDateString(),
              education: data.education || "",
              language: data.language || "",
              skills,
              profilePicture: data.avatar_url || "",
              coverPhoto: data.coverPhoto || "/lovable-uploads/63560c3c-d7c3-4d91-b852-d2cd79229bf8.png",
            });
            
            // Always load approved courses for the profile being viewed
            loadApprovedCourses(profileId);
          }
        } catch (e) {
          console.error("Error loading profile data:", e);
        } finally {
          setIsLoading(false);
        }
      } else {
        // Only redirect to login if trying to view own profile without being logged in
        if (!id) {
          toast({
            title: "Authentication Required",
            description: "Please log in to view your profile.",
            variant: "destructive",
          });
          navigate("/login");
        }
        setIsLoading(false);
      }
    };
    
    loadUserData();
  }, [user, id, toast, navigate, authLoading]);

  // Set up real-time subscription for courses
  useEffect(() => {
    const profileId = id || user?.id;
    if (!profileId) return;

    const channel = supabase
      .channel('profile-courses-changes')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'courses',
        filter: `mentor_id=eq.${profileId}`
      }, (payload) => {
        console.log('Course change detected on profile:', payload);
        // Reload courses when any change occurs
        loadApprovedCourses(profileId);
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [id, user?.id]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setUserData({
      ...userData,
      [name]: value
    });
  };

  const addSkill = () => {
    if (newSkill.trim() && !userData.skills.includes(newSkill.trim())) {
      setUserData({
        ...userData,
        skills: [...userData.skills, newSkill.trim()]
      });
      setNewSkill("");
    }
  };

  const removeSkill = (skill: string) => {
    setUserData({
      ...userData,
      skills: userData.skills.filter(item => item !== skill)
    });
  };

  const saveProfile = async () => {
    localStorage.setItem("userData", JSON.stringify(userData));
    if (!user?.id) return setIsEditing(false);
    try {
      console.log("Saving profile with data:", {
        first_name: userData.firstName,
        last_name: userData.lastName,
        location: userData.location,
        profession: userData.occupation,
        bio: userData.bio,
        short_summary: userData.shortSummary,
        avatar_url: userData.profilePicture,
        coverPhoto: userData.coverPhoto,
        education: userData.education,
        language: userData.language,
        specialties: JSON.stringify(userData.skills),
      });
      
      const { error } = await supabase.from("profiles").update({
        first_name: userData.firstName,
        last_name: userData.lastName,
        location: userData.location,
        profession: userData.occupation,
        bio: userData.bio,
        short_summary: userData.shortSummary,
        avatar_url: userData.profilePicture,
        coverPhoto: userData.coverPhoto,
        education: userData.education,
        language: userData.language,
        specialties: JSON.stringify(userData.skills),
      }).eq("id", user.id);
      
      if (error) {
        console.error("Error saving profile:", error);
        toast({
          title: "Failed to save profile",
          description: error.message,
          variant: "destructive"
        });
      } else {
        console.log("Profile updated successfully");
        toast({
          title: "Profile Updated",
          description: "Your profile has been saved successfully.",
          variant: "default"
        });
        
        // Refresh auth context to ensure latest data
        if (refreshUser) {
          await refreshUser();
        }
      }
    } catch (e: any) {
      console.error("Exception saving profile:", e);
      toast({
        title: "Error saving profile",
        description: e?.toString?.() || "Unknown error",
        variant: "destructive"
      });
    }
    setIsEditing(false);
  };

  const aboutSection = (
    <Card className="mb-6">
      <CardContent className="p-6">
        <h2 className="text-xl font-semibold mb-4">About</h2>
        {isEditing ? (
          <textarea
            name="bio"
            value={userData.bio}
            onChange={handleInputChange}
            placeholder="Write a short bio about yourself..."
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-base min-h-[150px]"
          />
        ) : (
          <div>
            {userData.bio ? (
              formatBioText(userData.bio)
            ) : (
              <p className="text-gray-600">
                {isViewingOtherProfile ? "No bio available" : "Your bio appears here. Edit your profile to add a bio."}
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );

  const skillsSection = (
    <Card className="mb-6">
      <CardContent className="p-6">
        <h2 className="text-xl font-semibold mb-4">Skills</h2>
        {isEditing ? (
          <div className="space-y-3">
            <div className="flex gap-2">
              <Input 
                value={newSkill}
                onChange={(e) => setNewSkill(e.target.value)}
                placeholder="Add your skill"
                className="flex-1"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addSkill();
                  }
                }}
              />
              <Button onClick={addSkill} type="button">Add</Button>
            </div>
            <div className="flex flex-wrap gap-2 mt-3">
              {userData.skills.map((skill, index) => (
                <Badge key={index} variant="secondary" className="py-1.5 pl-2 pr-1 flex items-center gap-1">
                  {skill}
                  <button 
                    onClick={() => removeSkill(skill)}
                    className="ml-1 text-gray-500 hover:text-gray-700 rounded-full h-4 w-4 flex items-center justify-center"
                  >
                    ×
                  </button>
                </Badge>
              ))}
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {userData.skills && userData.skills.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {userData.skills.map((skill, index) => (
                  <Badge key={index} variant="secondary" className="py-1.5">
                    {skill}
                  </Badge>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 italic">
                {isViewingOtherProfile 
                  ? "No skills listed" 
                  : "No skills added yet. Edit your profile to add your skills."}
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );

  const shortSummarySection = isEditing && (
    <Card className="mb-6">
      <CardContent className="p-6">
        <Label htmlFor="shortSummary">Short One-Line Summary</Label>
        <Input
          id="shortSummary"
          name="shortSummary"
          value={userData.shortSummary}
          onChange={handleInputChange}
          maxLength={80}
          placeholder="One line about you (shown on profile cards)..."
          className="mt-2"
        />
      </CardContent>
    </Card>
  );

  const offersTitle = `What ${userData.firstName || "this mentor"} can offer you:`;

  const myAcademyShop = (
    <Card className="border-2 border-emu-blue shadow-xl">
      <CardHeader className="pb-3 bg-emu-blue/10 rounded-t-lg">
        <CardTitle className="text-xl font-semibold flex items-center gap-2 text-emu-blue">
          <BookOpen className="h-5 w-5" />
          {offersTitle}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {approvedCourses.length > 0 ? (
          <div className="flex flex-col gap-4">
            {approvedCourses.map((course) => (
              <CourseDialog
                key={course.id}
                course={course}
                mentorName={userData.firstName || "this mentor"}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-4 text-gray-500">
            <p>{isViewingOtherProfile ? "No courses available" : "No courses yet"}</p>
            {!isViewingOtherProfile && (
              <Button
                variant="link"
                className="text-emu-blue mt-2"
                onClick={() => navigate("/my-academy")}
              >
                Create a course in My Academy Shop
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );

  if (isLoading || authLoading) {
    return (
      <div className="min-h-screen flex flex-col bg-gray-50">
        <Navbar />
        <div className="flex-grow flex items-center justify-center">
          <div className="w-12 h-12 border-4 border-emu-blue border-t-transparent rounded-full animate-spin"></div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Navbar />
      
      <div className="w-full bg-cover bg-center h-64 sm:h-80 relative" style={{ backgroundImage: `url('${userData.coverPhoto}')` }}>
        <div className="absolute inset-0 bg-emu-blue/20"></div>
        
        {isEditing && (
          <Button 
            size="sm" 
            variant="outline"
            className="absolute top-4 right-4 bg-white/80 backdrop-blur-sm"
          >
            <Camera size={16} className="mr-1" />
            Change Cover
          </Button>
        )}
        
        <div className="container mx-auto h-full relative">
          <div className="absolute left-6 -bottom-16 sm:-bottom-20">
            <ProfilePictureUploader 
              userId={user?.id || ''}
              profilePicture={userData.profilePicture}
              firstName={userData.firstName}
              lastName={userData.lastName}
              onPictureUpdate={(url) => setUserData({ ...userData, profilePicture: url })}
              disabled={!isEditing || isViewingOtherProfile}
            />
          </div>
        </div>
        
        {isViewingOtherProfile && (
          <div className="absolute top-4 left-4">
            <Button 
              variant="outline" 
              className="bg-white/20 text-white hover:bg-white/30"
              asChild
            >
              <Link to="/search-mentors">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2"><path d="m15 18-6-6 6-6"/></svg>
                Back
              </Link>
            </Button>
          </div>
        )}
      </div>
      
      <main className="flex-grow pt-20 sm:pt-24 pb-12">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-5xl mx-auto">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8">
              <div>
                <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-1">
                  {isEditing ? (
                    <div className="flex gap-2">
                      <Input 
                        name="firstName"
                        value={userData.firstName} 
                        onChange={handleInputChange}
                        placeholder="First Name"
                        className="text-2xl h-auto py-1 font-bold"
                      />
                      <Input 
                        name="lastName"
                        value={userData.lastName} 
                        onChange={handleInputChange}
                        placeholder="Last Name"
                        className="text-2xl h-auto py-1 font-bold"
                      />
                    </div>
                  ) : (
                    `${userData.firstName || "Profile"} ${userData.lastName || ""}`
                  )}
                </h1>
                <div className="text-gray-500 flex items-center">
                  <div className="px-3 py-1 rounded-full bg-blue-100 text-emu-blue text-sm font-medium inline-flex items-center mr-3">
                    <User size={14} className="mr-1" />
                    Mentor
                  </div>
                  <div className="flex items-center text-sm">
                    <Calendar size={14} className="mr-1" />
                    Joined {userData.joinDate}
                  </div>
                </div>
                {!isEditing && userData.shortSummary && (
                  <div className="mt-2 block text-base text-emu-blue font-medium">{userData.shortSummary}</div>
                )}
              </div>
              
              {!isViewingOtherProfile && (
                <Button 
                  onClick={isEditing ? saveProfile : () => setIsEditing(true)}
                  className={`mt-4 sm:mt-0 ${isEditing ? "bg-green-600 hover:bg-green-700" : "bg-emu-blue hover:bg-emu-blue/90"}`}
                >
                  {isEditing ? (
                    <>
                      <Save size={16} className="mr-1" />
                      Save Profile
                    </>
                  ) : (
                    <>
                      <Edit size={16} className="mr-1" />
                      Edit Profile
                    </>
                  )}
                </Button>
              )}
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="md:col-span-2 flex flex-col">
                {aboutSection}
                {skillsSection}
                {shortSummarySection}
                <Card>
                  <CardContent className="p-6">
                    <h2 className="text-xl font-semibold mb-4">General Information</h2>
                    <div className="space-y-4">
                      {isEditing ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="location">Location</Label>
                            <div className="relative mt-1">
                              <MapPin size={16} className="absolute left-3 top-3 text-gray-400" />
                              <Input
                                id="location"
                                name="location"
                                value={userData.location}
                                onChange={handleInputChange}
                                placeholder="Your city, country"
                                className="pl-9"
                              />
                            </div>
                          </div>
                          <div>
                            <Label htmlFor="occupation">Occupation</Label>
                            <div className="relative mt-1">
                              <Briefcase size={16} className="absolute left-3 top-3 text-gray-400" />
                              <Input
                                id="occupation"
                                name="occupation"
                                value={userData.occupation}
                                onChange={handleInputChange}
                                placeholder="Your job title"
                                className="pl-9"
                              />
                            </div>
                          </div>
                          <div>
                            <Label htmlFor="education">Education</Label>
                            <div className="relative mt-1">
                              <GraduationCap size={16} className="absolute left-3 top-3 text-gray-400" />
                              <Input
                                id="education"
                                name="education"
                                value={userData.education}
                                onChange={handleInputChange}
                                placeholder="Your education"
                                className="pl-9"
                              />
                            </div>
                          </div>
                          <div>
                            <Label htmlFor="language">Language</Label>
                            <div className="relative mt-1">
                              <Languages size={16} className="absolute left-3 top-3 text-gray-400" />
                              <Input
                                id="language"
                                name="language"
                                value={userData.language}
                                onChange={handleInputChange}
                                placeholder="Languages you speak"
                                className="pl-9"
                              />
                            </div>
                          </div>
                        </div>
                      ) : (
                        <>
                          {userData.location && (
                            <div className="flex items-start">
                              <MapPin className="h-5 w-5 text-gray-400 mr-3 mt-0.5" />
                              <div>
                                <div className="text-sm font-medium text-gray-500">Location</div>
                                <div className="text-gray-700">{userData.location}</div>
                              </div>
                            </div>
                          )}
                          
                          {userData.occupation && (
                            <div className="flex items-start">
                              <Briefcase className="h-5 w-5 text-gray-400 mr-3 mt-0.5" />
                              <div>
                                <div className="text-sm font-medium text-gray-500">Occupation</div>
                                <div className="text-gray-700">{userData.occupation}</div>
                              </div>
                            </div>
                          )}
                          
                          {userData.education && (
                            <div className="flex items-start">
                              <GraduationCap className="h-5 w-5 text-gray-400 mr-3 mt-0.5" />
                              <div>
                                <div className="text-sm font-medium text-gray-500">Education</div>
                                <div className="text-gray-700">{userData.education}</div>
                              </div>
                            </div>
                          )}
                          
                          {userData.language && (
                            <div className="flex items-start">
                              <Languages className="h-5 w-5 text-gray-400 mr-3 mt-0.5" />
                              <div>
                                <div className="text-sm font-medium text-gray-500">Language</div>
                                <div className="text-gray-700">{userData.language}</div>
                              </div>
                            </div>
                          )}
                          
                          
                          {!userData.location && !userData.occupation && !userData.education && !userData.language && (
                            <p className="text-gray-500 italic">
                              {isViewingOtherProfile 
                                ? "No information provided." 
                                : "No information provided yet. Edit your profile to add details."}
                            </p>
                          )}
                        </>
                      )}
                    </div>
                  </CardContent>
                </Card>
                
                {/* Reviews Section - Added here below General Information */}
                <Card className="mt-6">
                  <CardContent className="p-6">
                    <ReviewsSection mentorId={id || user?.id || ''} />
                  </CardContent>
                </Card>
              </div>
              <div>
                {myAcademyShop}
              </div>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default MentorProfile;
